import React, { useState, useEffect, useMemo } from 'react'
import { Provider, useTable } from 'tinybase/ui-react'
import { SYNC_STATUS } from '../utils/subject_store'

const UnsyncedSubjectsContent = ({ projectId, subjectStore, onSyncSubject, onDeleteSubject, onSyncAll, onClearAll }) => {
  const [loading, setLoading] = useState(true)

  // Use TinyBase hook to trigger updates when subjects change
  const subjectsTable = useTable('subjects')

  // Use state to hold subjects from the store's getAll method
  const [subjects, setSubjects] = useState([])

  // Load subjects when tables change using the store's getAll method
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const subjectData = await subjectStore.getAll(projectId)
        setSubjects(subjectData)
      } catch (error) {
        console.error('Error loading subjects:', error)
        setSubjects([])
      }
    }

    // Load subjects when subjects table changes (maintains reactivity)
    if (subjectsTable !== undefined) {
      loadSubjects()
    }
  }, [subjectsTable, projectId, subjectStore])

  // Calculate queue stats from subjects
  const queueStats = useMemo(() => {
    const subjectValues = subjects || []
    const stats = {
      totalItems: subjectValues.length,
      processing: 0,
      completed: 0,
      failed: 0,
      pending: 0
    }

    subjectValues.forEach(subject => {
      switch (subject.syncStatus) {
        case SYNC_STATUS.PENDING:
          stats.pending++
          break
        case SYNC_STATUS.IN_PROGRESS:
          stats.processing++
          break
        case SYNC_STATUS.COMPLETED:
          stats.completed++
          break
        case SYNC_STATUS.FAILED:
          stats.failed++
          break
        default:
          stats.pending++ // Treat "Ready" as pending
          break
      }
    })

    return stats
  }, [subjects])

  // Wait for store to be ready on mount
  useEffect(() => {
    const initializeStore = async () => {
      try {
        setLoading(true)
        await subjectStore.waitForReady()
      } catch (error) {
        console.error('Error initializing store:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeStore()
  }, [subjectStore, projectId])

  const handleSyncSubject = async (subjectId) => {
    try {
      await onSyncSubject(subjectId)
    } catch (error) {
      console.error('Error syncing subject:', error)
    }
  }

  const handleDeleteSubject = async (subjectId) => {
    if (!confirm('Are you sure you want to delete this subject? This action cannot be undone.')) {
      return
    }

    try {
      await onDeleteSubject(subjectId)
      // TinyBase reactivity will handle the UI update automatically
      // No need to manually refresh subjects list
    } catch (error) {
      console.error('Error deleting subject:', error)
      // Optionally show a toast notification to the user
      if (window.showToast) {
        window.showToast('Failed to delete subject. Please try again.', 'error')
      }
    }
  }

  const handleSyncAll = async () => {
    try {
      await onSyncAll()
      // No need to manually refresh - TinyBase hooks will auto-update
    } catch (error) {
      console.error('Error syncing all subjects:', error)
    }
  }

  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to delete all subjects in the sync queue? This action cannot be undone.')) {
      return
    }

    try {
      await onClearAll()
      // TinyBase reactivity will handle the UI update automatically
      // No need to manually refresh subjects list
    } catch (error) {
      console.error('Error clearing all subjects:', error)
      // Optionally show a toast notification to the user
      if (window.showToast) {
        window.showToast('Failed to clear subjects. Please try again.', 'error')
      }
    }
  }

  if (subjects.length === 0) {
    return null
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  return (
    <div className="bg-blue-100 rounded-lg collapse collapse-arrow bg-base-100 hover:shadow-md">
      <input type="checkbox" />
      <div className="flex items-center collapse-title">
        {queueStats && queueStats.processing > 0 ? (
          <span className="loading loading-spinner loading-sm text-blue-600 mr-2"></span>
        ) : (
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        )}
        <h3 className="display4">
          Sync Queue
          <div className="badge badge-warning mx-2 text-base-content font-normal">
            {subjects.length} subjects pending
          </div>
        </h3>
      </div>

      <div className='collapse-content'>

        <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-end mb-4">
          <button
            onClick={handleSyncAll}
            className="btn btn-primary btn-wide"
          >
            {queueStats && queueStats.processing > 0 ? (
              <span className="loading loading-spinner loading-xs mr-1"></span>
            ) : (
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {queueStats && queueStats.processing > 0 ? 'Syncing...' : 'Sync All'}
          </button>

          <button
            onClick={handleClearAll}
            className="btn btn-error btn-outline btn-wide"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete All
          </button>
        </div>

        <div className="space-y-3">
          {subjects.map(subject => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              onSync={() => handleSyncSubject(subject.id)}
              onDelete={() => handleDeleteSubject(subject.id)}
            />
          ))}
        </div>

      </div>
    </div>
  )
}

const SubjectCard = ({ subject, onSync, onDelete }) => {
  const [photoDataUrls, setPhotoDataUrls] = useState([])
  const [isVisible, setIsVisible] = useState(true)
  const [isFadingOut, setIsFadingOut] = useState(false)

  const createdDate = new Date(subject.createdAt).toLocaleDateString()
  const hasAudio = subject.hasAudio ? 'Yes' : 'No'

  // Load photo data URLs from the subject's photosAsync promise
  useEffect(() => {
    const loadPhotoDataUrls = async () => {
      try {
        const dataUrls = await subject.photosAsync
        setPhotoDataUrls(dataUrls || [])
      } catch (error) {
        console.error('Error loading photo data URLs:', error)
        setPhotoDataUrls([])
      }
    }

    if (subject.photosAsync) {
      loadPhotoDataUrls()
    }
  }, [subject.photosAsync])

  // Handle fade-out for completed subjects
  useEffect(() => {
    if (subject.syncStatus === SYNC_STATUS.COMPLETED) {
      // Start fade-out after a brief delay to show completion
      const fadeTimer = setTimeout(() => {
        setIsFadingOut(true)
      }, 1000) // 1 second to show completion state

      // Hide completely after fade transition (aligned with store deletion)
      const hideTimer = setTimeout(() => {
        setIsVisible(false)
      }, 3000) // 3 seconds total (1s delay + 2s fade)

      return () => {
        clearTimeout(fadeTimer)
        clearTimeout(hideTimer)
      }
    }
  }, [subject.syncStatus])

  // Don't render if not visible
  if (!isVisible) {
    return null
  }


  const getSyncStatusColor = () => {
    switch (subject.syncStatus) {
      case SYNC_STATUS.PENDING:
        return 'warning'
      case SYNC_STATUS.IN_PROGRESS:
        return 'info'
      case SYNC_STATUS.COMPLETED:
        return 'success'
      case SYNC_STATUS.FAILED:
        return 'error'
      default:
        return 'default'
    }
  }

  const getSyncStatusText = () => {
    switch (subject.syncStatus) {
      case SYNC_STATUS.PENDING:
        return 'Pending'
      case SYNC_STATUS.IN_PROGRESS:
        return `Syncing... ${subject.progress}%`
      case SYNC_STATUS.COMPLETED:
        return 'Completed'
      case SYNC_STATUS.FAILED:
        return `Failed`
      default:
        return 'Ready'
    }
  }

  return (
    <div className={`rounded-lg p-3 transition-all duration-2000 ease-out bg-base-100 ${isFadingOut ? 'opacity-0 scale-95 transform translate-y-4' : 'opacity-100 scale-100'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex sm:items-center justify-between w-full gap-3 sm:flex-row flex-col">

          <div className="flex flex-col gap-1">
            <h4 className="text-lg font-medium text-base-content">Subject {subject.id.split('_')[1]}</h4>
            <p>Created: {createdDate}</p>
            <p>{photoDataUrls.length} photos </p>
            <p>Audio: {hasAudio}</p>

            <p className="flex items-center gap-1">
              Status:
              <span className={`text-base-content badge-sm badge badge-${getSyncStatusColor()} ${subject.syncStatus === SYNC_STATUS.IN_PROGRESS ? 'loading loading-spinner loading-xs' : ''}`}>{getSyncStatusText()}</span>
            </p>
          </div>


          <div className='flex flex-col gap-2'>
            <div className="flex flex-row gap-2 sm:justify-end flex-wrap">
              {photoDataUrls.length > 0 ? (
                photoDataUrls.map((photo, index) => {
                  return (<img
                    key={index}
                    src={photo.dataUrl}
                    alt="Primary photo"
                    className="w-21 h-21 object-cover rounded border border-gray-200"
                  />)
                })
              ) : (
                <svg className="w-21 h-21 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>


            <div className="flex flex-row items-center gap-2 justify-end">
              <button
                onClick={onSync}
                disabled={subject.syncStatus === SYNC_STATUS.IN_PROGRESS || subject.syncStatus === SYNC_STATUS.COMPLETED}
                className="btn btn-primary"
              >
                {subject.syncStatus === SYNC_STATUS.IN_PROGRESS && (
                  <span className="loading loading-spinner loading-xs mr-1"></span>
                )}
                {subject.syncStatus === SYNC_STATUS.IN_PROGRESS ? 'Syncing...' :
                subject.syncStatus === SYNC_STATUS.COMPLETED ? 'Synced' :
                subject.syncStatus === SYNC_STATUS.FAILED ? 'Retry' : 'Sync'}
              </button>

              <button
                onClick={onDelete}
                className="btn btn-error btn-outline"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar for in-progress syncing */}
      {subject.syncStatus === SYNC_STATUS.IN_PROGRESS && (
        <div className="mb-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${subject.progress || 0}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  )
}

// Wrapper component with TinyBase Provider
const UnsyncedSubjects = (props) => {
  const { subjectStore } = props
  return (
    <Provider store={subjectStore.getStore()}>
      <UnsyncedSubjectsContent {...props} />
    </Provider>
  )
}

export default UnsyncedSubjects